'use client';
import React, { useEffect, useRef } from 'react';
import DashboardPreview from './DashboardPreview';
import MintCounter from './MintCounter';

export default function HeroSection() {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const elements = [titleRef.current, subRef.current, ctaRef.current];
    elements.forEach((el, i) => {
      if (!el) return;
      el.classList.add('reveal-active');
      (el as HTMLElement).style.transitionDelay = `${0.2 + i * 0.15}s`;
    });
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Ambient blobs */}
      <div
        className="blob w-[600px] h-[600px] left-[-100px] top-[-100px] opacity-20"
        style={{ background: 'radial-gradient(circle, rgba(61,255,196,0.4) 0%, transparent 70%)' }} />

      <div
        className="blob w-[500px] h-[500px] right-[5%] bottom-[10%] opacity-15"
        style={{ background: 'radial-gradient(circle, rgba(180,160,255,0.5) 0%, transparent 70%)', animationDelay: '4s' }} />


      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-28 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center min-h-[80vh]">

          {/* LEFT: Dashboard Preview */}
          <div className="flex flex-col items-center lg:items-start gap-6">
            <div className="reveal-up is-visible delay-100 w-full flex justify-center lg:justify-start">
              <span className="badge-mint text-xs font-display font-700 px-3 py-1.5 rounded-full uppercase tracking-wider">
                Live Platform Preview
              </span>
            </div>
            <div className="reveal-up is-visible delay-200 w-full flex justify-center lg:justify-start">
              <DashboardPreview />
            </div>
            <p className="reveal-up is-visible delay-300 text-xs text-white/30 font-body text-center lg:text-left">
              ↑ Real incidents, real visitors — feed updates live as luck happens
            </p>
          </div>

          {/* RIGHT: Headline + CTA */}
          <div className="flex flex-col gap-8 lg:pl-8">
            <h1
              ref={titleRef}
              className="font-display font-700 text-[clamp(2.4rem,5vw,4rem)] leading-[1.05] tracking-tight text-iwhite"
              style={{ transitionDelay: '0.2s' }}>

              <span className="text-reveal-wrapper">
                <span className="text-reveal-content">The world logs its</span>
              </span>
              <span className="text-reveal-wrapper">
                <span className="text-reveal-content delay-100" style={{ color: '#3DFFC4' }}>luck incidents</span>
              </span>
              <span className="text-reveal-wrapper">
                <span className="text-reveal-content delay-200">here, in real-time.</span>
              </span>
            </h1>

            {/* Subheadline */}
            <div ref={subRef} className="reveal-up">
              <p className="text-lg text-white/60 font-body leading-relaxed max-w-md">
                Unique visitors from every corner of the globe post their lucky escapes, windfalls, and serendipitous moments — building the world&apos;s first <span className="text-mint font-medium">live luck map.</span>
              </p>
            </div>

            {/* Counter */}
            <div className="reveal-up is-visible delay-300">
              <MintCounter />
            </div>

            {/* CTA Block */}
            <div ref={ctaRef} className="reveal-up flex flex-col gap-3">
              <a
                href="#"
                className="cta-primary mint-glow inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-display font-700 w-fit">

                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
                Log Your Luck Incident
              </a>
              <a href="#" className="text-sm text-white/40 hover:text-white/70 transition-colors font-body underline underline-offset-4 w-fit">
                Browse the live feed →
              </a>
            </div>

            {/* Trust badges */}
            <div className="reveal-up is-visible delay-400 flex items-center gap-4 pt-2">
              {[
              { label: 'Anonymous posting', icon: '🔓' },
              { label: 'Always free', icon: '⚡' },
              { label: 'Post in 30s', icon: '🍀' }].
              map((b) =>
              <div key={b.label} className="flex items-center gap-1.5">
                  <span className="text-sm">{b.icon}</span>
                  <span className="text-xs text-white/40 font-body">{b.label}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
      style={{ background: 'linear-gradient(to bottom, transparent, #0D0F1C)' }} />

    </section>);

}